import { Position } from 'geojson';
import { Keyboard, Linking } from 'react-native';
import Geolocation from 'react-native-geolocation-service';
import { Region } from 'react-native-maps';
import { mapDirectionsTarget } from 'states/map';
import { useActiveElementStore } from 'stores/activeElementStore';
import { useCityParkingStore } from 'stores/cityParkingStore';
import { useMapStore } from 'stores/mapStore';
import { useParkingStore } from 'stores/parkingStore';
import { useParkzoneStore } from 'stores/parkzoneStore';
import { usePermitStore } from 'stores/permitStore';
import { useUiStore } from 'stores/uiStore';
import { ParkingZoneType } from 'types/common';
import { CountryCode } from 'types/generatedSchemaTypes';
import { LatLng } from 'types/MapTypes';
import { isAndroid, isIos, isWeb } from 'utils/deviceUtils';

export interface MarkerIconsMapProps {
  [markerType: string]: string;
}
export const markerIconsMap: MarkerIconsMapProps = {
  garage: 'MarkerGarageActive',
  default: 'MarkerOutdoorParkingActive',
  anpr: 'MarkerCameraRecognitionActive',
  evCharging: 'MarkerEVChargeActive',
  ride: 'MarkerRideActive',
  scooter: 'MarkerScooterActive',
  subway: 'MarkerSubwayActive',
  free: 'MarkerFreeActive',
  boat: 'MarkerBoatActive',
  skistar: 'MarkerSkistarActive',
};

export const mapGeoJSONToPolygonArray = (area: Position[][][]) => {
  let mappedPolygonArray: LatLng[][] = [];
  if (area?.length > 0) {
    mappedPolygonArray = area.map((firstLevel: Position[][]) => {
      // skip second level
      const mappedPoints: LatLng[] = firstLevel[0]?.map(
        (thirdLevel: number[]) => {
          return { longitude: thirdLevel[0], latitude: thirdLevel[1] };
        }
      );
      return mappedPoints;
    });
  }
  return mappedPolygonArray;
};

export const setParkingZoneReactiveVars = (
  parkingZoneUid = '',
  type: ParkingZoneType = '',
  secondaryUid = ''
) => {
  useActiveElementStore
    .getState()
    .setActiveElementSelection(parkingZoneUid, type, secondaryUid);
  useParkzoneStore.getState().setSelectedTariffName(undefined);
  useParkzoneStore.getState().setSelectedTariffId(undefined);
  useParkingStore.getState().setHasManuallySelectedTariff(false);
  useParkzoneStore
    .getState()
    .setSelectedChargingPricingInfoSegmentTariffPlanId(undefined);
  useParkzoneStore.getState().setSelectedParkingZoneTab(undefined);
};

export const handleParkZoneSelected = (
  parkZoneUid: string,
  coordinates: LatLng[] | LatLng | undefined,
  withLTP: boolean,
  permitForParkZone: string | undefined,
  isStreet?: boolean
) => {
  // Show warning and skip the flow
  // if current view is LTP and the user has already a permit in the parking zone.
  if (withLTP && !!permitForParkZone) {
    handleElPress(parkZoneUid, coordinates);
    usePermitStore.getState().setSelectedPermit(permitForParkZone);
    return;
  }
  handleElPress(parkZoneUid, isStreet ? undefined : coordinates);
};

export const handleElPress = (
  id: string,
  coordinates: LatLng[] | LatLng | undefined
) => {
  if (useActiveElementStore.getState().activeElement === id) {
    setParkingZoneReactiveVars();
  } else {
    setParkingZoneReactiveVars(id, 'PARKING');
  }
  if (coordinates) {
    useMapStore.getState().setMapCoordinates(coordinates);
  }
  Keyboard.dismiss();
};

/**
 * Returns latitude and longitude object from coordinates array
 * @param c Position as number array
 * @returns Position as object
 */
export const makePoint = (c: Position) => ({
  latitude: c[1],
  longitude: c[0],
});

export const getDistanceFromLatLonInMeters = (
  { latitude: lat1, longitude: lon1 }: LatLng,
  { latitude: lat2, longitude: lon2 }: LatLng
) => {
  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1); // deg2rad below
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) *
      Math.cos(deg2rad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c * 1000; // Distance in m
  // round to closest m
  return Math.round(d);
};

const deg2rad = (deg: number) => {
  return deg * (Math.PI / 180);
};

export const checkLocationPermissions = async () => {
  if (isWeb) {
    const success = () => false;
    const error = () => false;
    navigator.geolocation.getCurrentPosition(success, error, {
      enableHighAccuracy: true,
    });
    return '';
  }
  let access = '';
  import('react-native-permissions').then(async (Permissions) => {
    if (isIos) {
      try {
        access = await Permissions.check('ios.permission.LOCATION_WHEN_IN_USE');
      } catch {
        access = 'unavailable';
      }
    }
    if (isAndroid) {
      try {
        access = await Permissions.check(
          'android.permission.ACCESS_COARSE_LOCATION'
        );
      } catch (e) {
        access = 'unavailable';
      }
    }

    // this will open settings in both Android and IOS in cases needed
    if (
      access === 'unavailable' ||
      access === 'denied' ||
      access === 'blocked'
    ) {
      useMapStore.getState().setUserLocation(null);
      import('react-native-permissions').then(({ openSettings }) => {
        openSettings();
      });
    }

    // just to make sure gps is not turned off and to get dialog if it is on Android
    if (isAndroid) {
      const success = () => false;
      const error = () => false;
      Geolocation.getCurrentPosition(success, error, {
        enableHighAccuracy: true,
        showLocationDialog: true,
      });
    }
  });
  return access;
};

export const getGoogleMapsDirections = (label: string, latLng: string) => {
  if (!label) {
    return `https://www.google.com/maps/dir/?api=1&destination=${latLng}`;
  }
  return `https://www.google.com/maps/dir/?api=1&destination=${latLng}&query=${label}`;
};

export const getAppleMapsDirections = (label: string, latLng: string) => {
  const query = label || latLng;
  return `https://maps.apple.com/?daddr=${latLng}&q=${query}`;
};

const openMapUrl = (url: string) => {
  Linking.openURL(url).catch(() => undefined);
};

export const openMapDirections = (label: string, latLng: string) => {
  if (isIos) {
    mapDirectionsTarget({ label, latLng });
    useUiStore.getState().setSheetModal('openInMaps');
    return;
  }
  openMapUrl(getGoogleMapsDirections(label, latLng));
};

export const navigateWithCoordinates = (
  coordinateInfo: Position | undefined,
  destinationName = ''
) => {
  const coordinates = coordinateInfo && makePoint(coordinateInfo);
  if (!coordinates) {
    return;
  }
  const latLng = `${coordinates.latitude},${coordinates.longitude}`;
  const label = encodeURIComponent(destinationName);
  openMapDirections(label, latLng);
};

export type BoxDimensions = {
  upperRightLat: number;
  upperRightLng: number;
  bottomLeftLat: number;
  bottomLeftLng: number;
};

export type DimensionWrapper = {
  viewPort: BoxDimensions;
  outerBox: BoxDimensions;
  zoom: boolean;
};

export const getBoxDimensionsFromRegion = (
  region: Region
): DimensionWrapper => {
  const viewPort = {
    upperRightLat: region.latitude + region.latitudeDelta / 2,
    upperRightLng: region.longitude + region.longitudeDelta / 2,
    bottomLeftLat: region.latitude - region.latitudeDelta / 2,
    bottomLeftLng: region.longitude - region.longitudeDelta / 2,
  };

  const outerBox = {
    upperRightLat:
      region.latitude + (region.latitudeDelta * (isWeb ? 4 : 2)) / 3,
    upperRightLng:
      region.longitude + (region.longitudeDelta * (isWeb ? 4 : 2)) / 3,
    bottomLeftLat:
      region.latitude - (region.latitudeDelta * (isWeb ? 4 : 2)) / 3,
    bottomLeftLng:
      region.longitude - (region.longitudeDelta * (isWeb ? 4 : 2)) / 3,
  };

  const zoom = region ? region.latitudeDelta > 0.01 : true;

  return {
    viewPort,
    outerBox,
    zoom,
  };
};

export const defaultRegionByAppLocation = (
  appLocation: CountryCode | null,
  loggedIn: boolean
) => {
  if (!appLocation || !loggedIn) {
    return {
      latitude: 59.334591,
      longitude: 18.06324,
      latitudeDelta: 0.015,
      longitudeDelta: 0.0121,
    };
  }
  if (appLocation === 'FI') {
    return {
      latitude: 60.192059,
      longitude: 24.945831,
      latitudeDelta: 0.015,
      longitudeDelta: 0.0121,
    };
  }
  return {
    latitude: 59.334591,
    longitude: 18.06324,
    latitudeDelta: 0.015,
    longitudeDelta: 0.0121,
  };
};

export const openDirections = (
  name?: string | null,
  markerCoordinate?: LatLng | null
) => {
  if (!markerCoordinate) {
    return;
  }
  const latLng = `${markerCoordinate.latitude},${markerCoordinate.longitude}`;
  const label = encodeURIComponent(name ?? '');
  openMapDirections(label, latLng);
};

export const isSelectedElement = (id: string, activeId: string) => {
  const selectedPmc = useCityParkingStore.getState().selectedPmc;
  return activeId === id || selectedPmc?.physicalZoneUid === id;
};
